import React, { useState, useMemo, useCallback } from 'react';
import { useIngresos } from '../../../modules/ingresos/hooks/useIngresos';
import { useEgresos } from '../../../modules/egresos/hooks/useEgresos';
import { useInversiones } from '../../../modules/inversiones/hooks/useInversiones';
import { LoggerService } from '../../../shared/services/logger.service';
import AdvancedChart from '../../../components/feature/AdvancedChart';

interface ReportFilters {
  startDate: string;
  endDate: string;
  categories: string[];
  reportType: 'summary' | 'detailed' | 'comparison';
}

interface ReportMetrics {
  totalIngresos: number;
  totalEgresos: number;
  totalInversiones: number;
  balanceNeto: number;
  ahorro: number;
  porcentajeAhorro: number;
}

export const ModernReportesDashboard: React.FC = () => {
  const { ingresos, loading: loadingIngresos } = useIngresos();
  const { egresos, loading: loadingEgresos } = useEgresos();
  const { inversiones, loading: loadingInversiones } = useInversiones();

  const [filters, setFilters] = useState<ReportFilters>({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    categories: [],
    reportType: 'summary'
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const filteredData = useMemo(() => {
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    endDate.setHours(23, 59, 59, 999); // Final del día

    const filteredIngresos = ingresos.filter(ingreso => {
      const ingresoDate = new Date(ingreso.fecha);
      return ingresoDate >= startDate && ingresoDate <= endDate;
    });

    const filteredEgresos = egresos.filter(egreso => {
      const egresoDate = new Date(egreso.fecha);
      return egresoDate >= startDate && egresoDate <= endDate;
    });

    const filteredInversiones = inversiones.filter(inversion => {
      const inversionDate = new Date(inversion.fecha);
      return inversionDate >= startDate && inversionDate <= endDate;
    });

    return { filteredIngresos, filteredEgresos, filteredInversiones };
  }, [ingresos, egresos, inversiones, filters]);

  const reportMetrics = useMemo((): ReportMetrics => {
    const { filteredIngresos, filteredEgresos, filteredInversiones } = filteredData;
    
    const totalIngresos = filteredIngresos.reduce((sum, ingreso) => sum + ingreso.monto, 0);
    const totalEgresos = filteredEgresos.reduce((sum, egreso) => sum + egreso.monto, 0);
    const totalInversiones = filteredInversiones.reduce((sum, inversion) => sum + inversion.monto, 0);
    
    const balanceNeto = totalIngresos - totalEgresos - totalInversiones;
    const ahorro = totalIngresos - totalEgresos;
    const porcentajeAhorro = totalIngresos > 0 ? (ahorro / totalIngresos) * 100 : 0;

    return {
      totalIngresos,
      totalEgresos,
      totalInversiones,
      balanceNeto,
      ahorro,
      porcentajeAhorro
    };
  }, [filteredData]);

  const getCategoriesData = useCallback(() => {
    const { filteredIngresos, filteredEgresos } = filteredData;
    
    const ingresosCategories = filteredIngresos.reduce((acc, ingreso) => {
      acc[ingreso.categoria] = (acc[ingreso.categoria] || 0) + ingreso.monto;
      return acc;
    }, {} as Record<string, number>);

    const egresosCategories = filteredEgresos.reduce((acc, egreso) => {
      acc[egreso.categoria] = (acc[egreso.categoria] || 0) + egreso.monto;  
      return acc;
    }, {} as Record<string, number>);

    return { ingresosCategories, egresosCategories };
  }, [filteredData]);

  // Generar datos para gráficos de distribución
  const chartData = useMemo(() => {
    const { ingresosCategories, egresosCategories } = getCategoriesData();
    
    // Datos para gráfico de ingresos
    const colors = ['#10b981', '#059669', '#047857', '#065f46', '#064e3b'];
    const ingresosChartData = Object.entries(ingresosCategories).map(([categoria, monto], index) => ({
      label: categoria.charAt(0).toUpperCase() + categoria.slice(1),
      value: monto,
      color: colors[index % colors.length]
    }));

    // Datos para gráfico de egresos
    const egresosColors = ['#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d'];
    const egresosChartData = Object.entries(egresosCategories).map(([categoria, monto], index) => ({
      label: categoria.charAt(0).toUpperCase() + categoria.slice(1),
      value: monto,
      color: egresosColors[index % egresosColors.length]
    }));

    // Datos para gráfico de tendencia combinado
    const { filteredIngresos, filteredEgresos, filteredInversiones } = filteredData;
    const tendenciaData = [
      {
        label: 'Ingresos',
        value: filteredIngresos.reduce((sum, item) => sum + item.monto, 0),
        color: '#10b981'
      },
      {
        label: 'Egresos', 
        value: filteredEgresos.reduce((sum, item) => sum + item.monto, 0),
        color: '#ef4444'
      },
      {
        label: 'Inversiones',
        value: filteredInversiones.reduce((sum, item) => sum + item.monto, 0),
        color: '#3b82f6'
      }
    ];

    return {
      ingresos: ingresosChartData,
      egresos: egresosChartData,
      tendencia: tendenciaData
    };
  }, [filteredData, getCategoriesData]);

  const handleFilterChange = (field: keyof ReportFilters, value: string | string[]) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportToPDF = () => {
    LoggerService.info('Exporting report to PDF', 'ModernReportesDashboard', { filters, metrics: reportMetrics });
    // TODO: Implementar exportación PDF
    alert('Exportación PDF: Función por implementar');
  };

  const exportToExcel = () => {
    LoggerService.info('Exporting report to Excel', 'ModernReportesDashboard', { filters, metrics: reportMetrics });
    // TODO: Implementar exportación Excel
    alert('Exportación Excel: Función por implementar');
  };

  const exportToCSV = () => {
    LoggerService.info('Exporting report to CSV', 'ModernReportesDashboard', { filters, metrics: reportMetrics });
    
    const { filteredIngresos, filteredEgresos } = filteredData;
    
    // Agrupar por mes
    interface MonthData {
      ingresos: typeof filteredIngresos;
      egresos: typeof filteredEgresos;
      totalIngresos: number;
      totalEgresos: number;
    }
    
    const monthsMap = new Map<string, MonthData>();
    
    // Procesar ingresos
    filteredIngresos.forEach(ingreso => {
      const date = new Date(ingreso.fecha);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthsMap.has(monthKey)) {
        monthsMap.set(monthKey, {
          ingresos: [],
          egresos: [],
          totalIngresos: 0,
          totalEgresos: 0
        });
      }
      
      const monthData = monthsMap.get(monthKey);
      if (monthData) {
        monthData.ingresos.push(ingreso);
        monthData.totalIngresos += ingreso.monto;
      }
    });
    
    // Procesar egresos
    filteredEgresos.forEach(egreso => {
      const date = new Date(egreso.fecha);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthsMap.has(monthKey)) {
        monthsMap.set(monthKey, {
          ingresos: [],
          egresos: [],
          totalIngresos: 0,
          totalEgresos: 0
        });
      }
      
      const monthData = monthsMap.get(monthKey);
      if (monthData) {
        monthData.egresos.push(egreso);
        monthData.totalEgresos += egreso.monto;
      }
    });
    
    // Ordenar meses cronológicamente
    const sortedMonths = Array.from(monthsMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    
    // Crear CSV
    const csvRows: string[] = [];
    
    // Encabezados
    csvRows.push('Mes,Tipo,Fecha,Categoría,Descripción,Monto');
    
    sortedMonths.forEach(([monthKey, monthData]) => {
      const [year, month] = monthKey.split('-');
      const monthName = new Date(parseInt(year), parseInt(month) - 1, 1).toLocaleDateString('es-AR', { 
        year: 'numeric', 
        month: 'long' 
      });
      
      // Agregar ingresos del mes
      monthData.ingresos.forEach(ingreso => {
        const fecha = new Date(ingreso.fecha).toLocaleDateString('es-AR');
        const row = [
          monthName,
          'Ingreso',
          fecha,
          ingreso.categoria,
          ingreso.descripcion || '',
          ingreso.monto.toString()
        ].map(field => `"${field}"`).join(',');
        csvRows.push(row);
      });
      
      // Agregar egresos del mes
      monthData.egresos.forEach(egreso => {
        const fecha = new Date(egreso.fecha).toLocaleDateString('es-AR');
        const row = [
          monthName,
          'Egreso',
          fecha,
          egreso.categoria,
          egreso.descripcion || '',
          egreso.monto.toString()
        ].map(field => `"${field}"`).join(',');
        csvRows.push(row);
      });
      
      // Subtotales del mes
      csvRows.push('');
      csvRows.push(`"${monthName}","SUBTOTAL INGRESOS","","","",${monthData.totalIngresos}`);
      csvRows.push(`"${monthName}","SUBTOTAL EGRESOS","","","",${monthData.totalEgresos}`);
      csvRows.push(`"${monthName}","BALANCE MES","","","",${monthData.totalIngresos - monthData.totalEgresos}`);
      csvRows.push('');
    });
    
    // Totales generales
    csvRows.push('RESUMEN GENERAL');
    csvRows.push(`"","TOTAL INGRESOS","","","",${reportMetrics.totalIngresos}`);
    csvRows.push(`"","TOTAL EGRESOS","","","",${reportMetrics.totalEgresos}`);
    csvRows.push(`"","BALANCE TOTAL","","","",${reportMetrics.balanceNeto}`);
    
    // Crear archivo y descargar
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `reporte_financiero_${filters.startDate}_${filters.endDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    LoggerService.info('CSV export completed', 'ModernReportesDashboard');
  };

  const loading = loadingIngresos || loadingEgresos || loadingInversiones;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reportes Financieros</h1>
          <p className="text-gray-600 mt-1">Análisis detallado de tu situación financiera</p>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={exportToCSV}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md"
          >
            <i className="ri-file-text-line"></i>
            <span>Descargar CSV</span>
          </button>
          <button
            onClick={exportToPDF}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md"
          >
            <i className="ri-file-pdf-line"></i>
            <span>Exportar PDF</span>
          </button>
          <button
            onClick={exportToExcel}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md"
          >
            <i className="ri-file-excel-line"></i>
            <span>Exportar Excel</span>
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtros de Reporte</h2>
        
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha Fin
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Reporte
            </label>
            <select
              value={filters.reportType}
              onChange={(e) => handleFilterChange('reportType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="summary">Resumen</option>
              <option value="detailed">Detallado</option>
              <option value="comparison">Comparativo</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({
                startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0],
                categories: [],
                reportType: 'summary'
              })}
              className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Métricas Principales */}
      <div className="grid md:grid-cols-4 gap-6">
        {/* Total Ingresos */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Ingresos</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {formatCurrency(reportMetrics.totalIngresos)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-arrow-up-line text-green-600 text-xl"></i>
            </div>
          </div>
        </div>

        {/* Total Egresos */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Egresos</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                {formatCurrency(reportMetrics.totalEgresos)}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <i className="ri-arrow-down-line text-red-600 text-xl"></i>
            </div>
          </div>
        </div>

        {/* Total Inversiones */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Inversiones</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {formatCurrency(reportMetrics.totalInversiones)}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-line-chart-line text-blue-600 text-xl"></i>
            </div>
          </div>
        </div>

        {/* Balance Neto */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Balance Neto</p>
              <p className={`text-2xl font-bold mt-1 ${reportMetrics.balanceNeto >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(reportMetrics.balanceNeto)}
              </p>
            </div>
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${reportMetrics.balanceNeto >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
              <i className={`ri-scales-line text-xl ${reportMetrics.balanceNeto >= 0 ? 'text-green-600' : 'text-red-600'}`}></i>
            </div>
          </div>
        </div>
      </div>

      {/* Gráfico de Tendencia */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Tendencia Financiera</h2>
        </div>
        <div className="p-6">
          {chartData.tendencia.length > 0 ? (
            <AdvancedChart
              title="Distribución Financiera"
              data={chartData.tendencia}
              type="balance"
            />
          ) : (
            <div className="text-center py-12">
              <i className="ri-line-chart-line text-4xl text-gray-400 mb-4"></i>
              <p className="text-gray-600">No hay datos disponibles</p>
              <p className="text-sm text-gray-500 mt-1">Aplica filtros para ver la tendencia financiera</p>
            </div>
          )}
        </div>
      </div>

      {/* Distribución por Categorías */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Ingresos por Categoría */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Ingresos por Categoría</h2>
          </div>
          <div className="p-6">
            {chartData.ingresos.length > 0 ? (
              <AdvancedChart
                title="Ingresos por Categoría"
                data={chartData.ingresos}
                type="income"
              />
            ) : (
              <div className="text-center py-8">
                <i className="ri-pie-chart-line text-3xl text-gray-400 mb-2"></i>
                <p className="text-gray-600">No hay ingresos en el período seleccionado</p>
              </div>
            )}
          </div>
        </div>

        {/* Egresos por Categoría */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Egresos por Categoría</h2>
          </div>
          <div className="p-6">
            {chartData.egresos.length > 0 ? (
              <AdvancedChart
                title="Egresos por Categoría"
                data={chartData.egresos}
                type="expense"
              />
            ) : (
              <div className="text-center py-8">
                <i className="ri-pie-chart-line text-3xl text-gray-400 mb-2"></i>
                <p className="text-gray-600">No hay egresos en el período seleccionado</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resumen del Período */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Resumen del Período</h2>
          <p className="text-sm text-gray-600 mt-1">
            {formatDate(new Date(filters.startDate))} - {formatDate(new Date(filters.endDate))}
          </p>
        </div>
        <div className="p-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-wallet-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900">Ahorro</h3>
              <p className="text-2xl font-bold text-blue-600 mt-1">{formatCurrency(reportMetrics.ahorro)}</p>
              <p className="text-sm text-gray-600 mt-1">{reportMetrics.porcentajeAhorro.toFixed(1)}% de los ingresos</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-funds-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900">Ratio Gastos/Ingresos</h3>
              <p className="text-2xl font-bold text-purple-600 mt-1">
                {reportMetrics.totalIngresos > 0 ? ((reportMetrics.totalEgresos / reportMetrics.totalIngresos) * 100).toFixed(1) : 0}%
              </p>
              <p className="text-sm text-gray-600 mt-1">Porcentaje de gastos</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-pie-chart-2-line text-orange-600 text-2xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900">Ratio Inversión</h3>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {reportMetrics.totalIngresos > 0 ? ((reportMetrics.totalInversiones / reportMetrics.totalIngresos) * 100).toFixed(1) : 0}%
              </p>
              <p className="text-sm text-gray-600 mt-1">Porcentaje invertido</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};